/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import BookingAction from "store/booking/BookingAction";
import {RouteComponentProps, withRouter} from "react-router";
import useBookingPackagesView from "pages/Front/Booking/components/BookingPackageNew/hooks/useBookingPackagesView";
import BookingPackagesViewSkeleton
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewSkeleton";
import BookingPackagesViewFooter
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewFooter";
import BookingPackagesCreditsHeaderStandard
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesCreditsHeaderStandard";
import BookingPackagesDriverRowStandard
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesDriverRowStandard";
import CreditsInfo from "pages/Front/Booking/Credits/components/CreditsInfo";
import BookingTicketCreditChoiceHelpers
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingTicketCreditChoiceHelpers";
import {closeModal, openModal} from "helpers/actions";
import {CreditsExplainerPage} from "pages/Front/Booking/Credits/CreditCalculator/components/CreditsExplainerPage";
import {ModalSize} from "pages/Front/components/Popup";
import PackageTicketsBadge from "pages/Front/Booking/components/BookingPackageNew/components/PackageTicketsBadge";
import PackageCreditBadge from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditBadge";
import PackageCreditInfoPillow
    from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditInfoPillow";

export interface BookingPackagesBasicCreditsParams {}

interface BookingPackagesBasicCreditsProps extends RouteComponentProps<BookingPackagesBasicCreditsParams> {}

const BookingPackagesBasicCredits = (props: BookingPackagesBasicCreditsProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {
        availablePackageItems,
        availableCreditItems,
        selectedPackageItems,
        selectedPackageCreditItems,
        allFilled,
        handleRouteNavigation,
        onNextStepHandler,
        setDefaultPackageCreditItems,
        getPackageItemByDuration,
    } = useBookingPackagesView(props.history);

    const handleApplyToAll = () => {
        if (selectedPackageItems.length === 0) return;

        const firstPerson = selectedPackageItems[0];
        const firstPersonFullItem = availablePackageItems?.packageItems[firstPerson.person_type]?.find(i => i.id === firstPerson.id);
        const duration = firstPersonFullItem?.duration;
        const firstPersonCredit = selectedPackageCreditItems.find(c => c.personIndex === firstPerson.personIndex);

        const newPackageItems = selectedPackageItems.map((item, idx) => {
            if (idx === 0) return item;
            const sameTypeItem = getPackageItemByDuration(duration, item.person_type);
            if (sameTypeItem) {
                return {...item, id: sameTypeItem.id};
            }
            return item;
        });

        const newCreditItems = selectedPackageItems.map(item => {
            return {
                personIndex: item.personIndex,
                id: firstPersonCredit?.id || null,
                person_type: item.person_type,
                quantity: 1,
            };
        });

        dispatch(BookingAction.setSelectedPackageItems(newPackageItems));
        dispatch(BookingAction.setSelectedPackageCreditItems(newCreditItems));
    };

    const openCreditCalculatorModal = () => {
        dispatch(openModal(<CreditsExplainerPage onClose={() => dispatch(closeModal())} />, {modalSize: ModalSize.Scrollable}));
    };

    useEffect(() => {
        const shouldSetDefaultPackageCreditItems =
            availableCreditItems.length > 0 && selectedPackageItems.length > 0 && selectedPackageCreditItems.length === 0;

        if (!shouldSetDefaultPackageCreditItems) {
            return;
        }

        setDefaultPackageCreditItems(false, selectedPackageItems);
    }, [availableCreditItems]);

    // tODO: po dokonceni responzivity, upravit!!!
    if (!availablePackageItems?.packageItems || !selectedPackageItems) {
        return <BookingPackagesViewSkeleton className={"BookingPackagesBasicCredits"} />;
    }

    return (
        <div className={"BookingPackagesBasicCredits w-full"}>
            <div className={"border rounded-xl bg-white shadow w-full"}>
                <div
                    className={
                        "BookingPackagesBasicCreditsHeader hidden lg:grid grid-cols-[10fr_38fr_45fr_9fr] xl:grid-cols-[10fr_36fr_42fr_12fr] rounded-t-xl w-full bg-[#F5F5F566]/40 border-b border-[#D9D9D9] px-2 lg:px-3 xl:px-4 py-3 xl:py-4 gap-4"
                    }
                >
                    <div></div>
                    <div className={"text-center w-full"}>
                        <PackageTicketsBadge />
                        <PackageCreditInfoPillow color={"yellow"} title={t("Entry includes unlimited use of over 20 standard attractions.")} subtitle={""} />
                    </div>
                    <div className={"relative text-center w-full"}>
                        <span className={"absolute -left-2 top-0 hidden h-full border-l border-[#D9D9D9] lg:block"} aria-hidden />
                        <PackageCreditBadge />

                        <PackageCreditInfoPillow
                            color={"red"}
                            title={t("For the attractions of the Speed & Drift Zone") + ":"}
                            subtitle={"Go-Karting · Crazy Drift" + " \r\n" + t("Credits are personal and non-transferable")}
                        />
                    </div>
                    <div></div>
                </div>

                <div className={"flex flex-col px-2 lg:px-0 py-2 lg:py-6 w-full lg:space-y-2"}>
                    <BookingPackagesCreditsHeaderStandard />

                    {selectedPackageItems.length > 0 &&
                        selectedPackageItems.map((packageItemByPerson, index) => {
                            return (
                                <BookingPackagesDriverRowStandard
                                    key={packageItemByPerson.personIndex}
                                    i={index}
                                    index={packageItemByPerson.personIndex as number}
                                    personType={packageItemByPerson.person_type}
                                />
                            );
                        })}
                </div>

                {/* Actions */}
                <BookingTicketCreditChoiceHelpers openCreditCalculatorModal={openCreditCalculatorModal} handleApplyToAll={handleApplyToAll} />

                <CreditsInfo />

                <BookingPackagesViewFooter variant={"basic"} onBack={handleRouteNavigation} onContinue={onNextStepHandler} disabled={!allFilled} />
            </div>
        </div>
    );
};

export default withRouter(BookingPackagesBasicCredits);
